import { useToast } from '@lifebit-ai/lds'
import { useQueryClient } from '@tanstack/react-query'
import React, { useMemo } from 'react'

import AssignToModalView from 'components/AssignToModal/AssignToModal'
import { useBulkAssignExportRequests } from 'services/results/hooks/useBulkAssignExportRequests'
import { useBulkUnassignExportRequests } from 'services/results/hooks/useBulkUnassignExportRequests'
import { useGetAssignableAssignees } from 'services/results/hooks/useGetAssignableAssignees'
import { ResultsQueryKeys } from 'services/results/queryKeys'
import { useGetUserInfo } from 'services/user/hooks/useGetUserInfo'

interface AssignToModalProps {
  resultId: string
  open: boolean
  onClose: () => void
  assignedTo?: string
}

const AssignToModal: React.FC<AssignToModalProps> = ({ resultId, open, onClose, assignedTo }) => {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { data: actingUser } = useGetUserInfo()

  const getAssignableAssigneesQuery = useGetAssignableAssignees(resultId)

  const invalidateAssignmentQueries = (): void => {
    queryClient.invalidateQueries({ queryKey: ResultsQueryKeys.ResultsVerificationV2() })
    queryClient.invalidateQueries({ queryKey: ResultsQueryKeys.ResultDetails(resultId) })
    queryClient.invalidateQueries({ queryKey: ResultsQueryKeys.ResultsVerificationAssignees() })
  }

  const bulkAssignExportRequestsMutation = useBulkAssignExportRequests({
    onSuccess: () => {
      invalidateAssignmentQueries()
      toast({ message: 'Export requests assigned successfully' })
      onClose()
    }
  })

  const bulkUnassignExportRequestsMutation = useBulkUnassignExportRequests({
    onSuccess: () => {
      invalidateAssignmentQueries()
      toast({ message: 'Export requests unassigned successfully' })
      onClose()
    }
  })

  const options = useMemo(
    () => getAssignableAssigneesQuery.data?.data.map(item => ({ label: item.email, value: item._id })) ?? [],
    [getAssignableAssigneesQuery.data]
  )

  return (
    <AssignToModalView
      open={open}
      onClose={onClose}
      assignedTo={assignedTo}
      options={options}
      isLoadingOptions={getAssignableAssigneesQuery.isLoading}
      isError={getAssignableAssigneesQuery.isError}
      isSubmitting={bulkAssignExportRequestsMutation.isPending || bulkUnassignExportRequestsMutation.isPending}
      onAssign={(assigneeId, reviewConfirmed) =>
        bulkAssignExportRequestsMutation.mutate({ resultIds: [resultId], assigneeId, reviewConfirmed })
      }
      onUnassign={() => bulkUnassignExportRequestsMutation.mutate({ resultIds: [resultId] })}
      actingUserId={actingUser?._id}
    />
  )
}

export default AssignToModal
